import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import ConfirmModal from "../../../global/components/ConfirmModal";
import AccountCreationModal from "../../../global/components/AccountCreationModal";
import LicenseIcon from "../../../assets/images/vectors/SideBarIcons/LicenseIcon";
import OemIcon from "../../../assets/images/vectors/SideBarIcons/OemIcon";
import { getOemNo, hasRole } from "../../../global/hooks/getUserRole";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { LicenseItemsColumns } from "./LicenseItemsColumns";
import LicenseItemModal from "./LicenseItemModal";

export const LicenseForm = ({
  type,
  isModalForm,
  onCancel,
  setDefaultData,
}) => {
  const isSystemUser = hasRole(1, 2, 3);
  const isInfoPage = type === "info";
  const [showModal, setShowModal] = useState(false);
  const [showItemsDeleteModal, setShowItemsDeleteModal] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [createdOem, setCreatedOem] = useState({ OemNo: null });

  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/licenses/${id}/`
    : undefined;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/licenses/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/licenses/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/licenses/${id}/`;
  const dealerOptionUrl = isSystemUser
    ? `${process.env.REACT_APP_API_URL}/api/dealers/options/`
    : undefined;
  const itemsUrl =
    id && isInfoPage
      ? `${process.env.REACT_APP_API_URL}/api/licenses/${id}/items/`
      : null;
  const deleteItemsUrl = id
    ? `${process.env.REACT_APP_API_URL}/api/licenses/${id}/items/bulk-delete/`
    : `${process.env.REACT_APP_API_URL}/api/licenses/0/items/bulk-delete/`;
  const itemOptionUrl = isInfoPage
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`
    : undefined;

  const { data, loading, refetch } = useGet(url);
  const {
    data: dealerOptionsData,
    loading: dealerOptionsLoading,
    refetch: dealerOptionsRefetch,
  } = useGet(dealerOptionUrl);
  const {
    data: itemsData,
    loading: itemsLoading,
    refetch: itemsRefetch,
  } = useGet(itemsUrl);
  const { data: itemOptionsData } = useGet(itemOptionUrl);
  const {
    data: postData,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const { data: putData, error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);
  const {
    data: deletedItemsData,
    error: deletedItemsDataError,
    deleteMultiple: deleteItemsData,
  } = useDelete(deleteItemsUrl);

  const licenseOemNo = data?.data?.OemNo ?? (isSystemUser ? null : getOemNo());

  const oemUrl =
    id && isInfoPage && licenseOemNo
      ? `${process.env.REACT_APP_API_URL}/api/oems/${licenseOemNo}/`
      : null;
  const { data: oemData, loading: oemLoading } = useGet(oemUrl);
  const oemInfo = oemData?.data ?? oemData;
  const isOemLicense = Number(oemInfo?.OemLicense) === 1;

  const modifiedOems = getModifiedResponse(dealerOptionsData, "oems");
  // const modifiedItems = getModifiedResponse(itemOptionsData, "items");

  const handleCreateNew = (modal) => {
    setModalType(modal);
    setIsOpenModal(true);
  };

  const onFinish = async (values) => {
    const payload = {
      LicenseName: values.LicenseName,
      LicenseDesc: values.LicenseDesc || "",
      OemNo: isSystemUser
        ? values.OemNo === "null"
          ? null
          : (values.OemNo ?? null)
        : getOemNo(),
    };

    try {
      if (id) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    isModalForm ? onCancel() : navigate("/license");
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  const onDeleteItemsConfirm = async () => {
    try {
      await deleteItemsData({ ids: selectedRowKeys });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowItemsDeleteModal(false);
    setSelectedRowKeys([]);
  };

  const itemRowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    fixed: true,
  };

  const openItemModal = (record = null) => {
    setEditingItem(record);
    setItemModalOpen(true);
  };

  const itemButtonsData = [
    {
      label: "Add",
      icon: <PlusOutlined />,
      onClick: () => openItemModal(null),
      disabled: oemLoading,
    },
    {
      label: "Delete",
      icon: <DeleteOutlined />,
      onClick: () => setShowItemsDeleteModal(true),
      disabled: selectedRowKeys?.length === 0,
    },
  ];

  useEffect(() => {
    if (!id || loading) return;
    try {
      form.resetFields();
      if (data?.data) {
        form.setFieldsValue({
          LicenseName: String(data.data.LicenseName),
          LicenseDesc: data.data.LicenseDesc
            ? String(data.data.LicenseDesc)
            : "",
          OemNo: data.data.OemNo ?? "null",
          Price:
            data.data.Price != null && data.data.Price !== ""
              ? parseFloat(data.data.Price).toFixed(2)
              : data.data.Price,
        });
      }
    } catch (error) {
      if (!loading) {
        openErrorNotification("Error fetching data!");
      }
    }
  }, [id, data, loading, form]);

  useEffect(() => {
    if (createdOem?.OemNo) {
      dealerOptionsRefetch && dealerOptionsRefetch();
      form.setFieldsValue({ OemNo: createdOem.OemNo });
    }
  }, [createdOem?.OemNo]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postError && postData) {
      openConfirmNotification("Success");
      setDefaultData &&
        setDefaultData({
          LicenseNo: postData?.data?.LicenseNo,
        });
      setTimeout(() => {
        if (isModalForm) {
          onCancel();
        } else {
          const licenseNo = postData?.data?.LicenseNo;
          navigate(licenseNo ? `/license/info/${licenseNo}` : -1);
        }
      }, 200);
    }
  }, [postError, postData]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        if (isModalForm) {
          onCancel();
        } else {
          navigate(`/license/info/${id}`);
        }
      }, 200);
    }
  }, [putData, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/license");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (deletedItemsData && !deletedItemsDataError) {
      openConfirmNotification("Success");
      itemsRefetch();
      refetch();
    } else if (deletedItemsDataError) {
      openErrorNotification(
        deletedItemsDataError?.response?.data?.message ||
          "Something went wrong",
      );
    }
  }, [deletedItemsData, deletedItemsDataError]);

  const breadcrumbItems = [
    {
      label: "License",
      href: "/license",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/license/info/${id}`
          : `/license/edit/${id}`
        : "/license/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => navigate(`/license/edit/${id}`),
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => setShowModal(true),
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => navigate("/license"),
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <div className="border bg-white rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <LicenseIcon className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                License
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="LicenseName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Description"
                    name="LicenseDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>
                {isSystemUser && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="OEM"
                      name="OemNo"
                      options={[
                        { label: "Generic", value: "null" },
                        ...modifiedOems,
                      ]}
                      placeholder="Please select"
                      disabled={type === "info"}
                      allowClear={false}
                      // required
                      handleCreateNew={
                        type !== "info" && !isModalForm ? handleCreateNew : null
                      }
                      type={"OEM"}
                      loading={loading || dealerOptionsLoading}
                    />
                  </Col>
                )}
                {isInfoPage && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomInput
                      label="Price"
                      name="Price"
                      disabled
                      type="number"
                      prefix={"$"}
                      style={{ height: "30px" }}
                      loading={loading}
                    />
                  </Col>
                )}
              </Row>
            </div>
          </div>
        </div>
      </Form>

      {id && isInfoPage ? (
        <>
          <div className="flex justify-end mb-2">
            <CommonButton buttonsData={itemButtonsData} />
          </div>
          <GlobalTable
            columns={LicenseItemsColumns()}
            dataSource={
              itemsData?.data
                ?.map((item) => ({
                  ...item,
                  key: item.ItemNo,
                }))
                ?.sort((a, b) => b.ItemNo - a.ItemNo) ?? []
            }
            rowSelection={itemRowSelection}
            onRow={(record) => ({
              onClick: (event) => {
                const clickedSelectionControl =
                  event.target.closest(".ant-table-selection-column") ||
                  event.target.closest(".ant-checkbox-wrapper") ||
                  event.target.closest(".ant-checkbox-input");
                if (clickedSelectionControl) return;
                if (oemLoading) return;
                openItemModal(record);
              },
              style: { cursor: "pointer" },
            })}
            loading={itemsLoading}
          />
          <LicenseItemModal
            open={itemModalOpen}
            setOpen={setItemModalOpen}
            licenseId={id}
            editingItem={editingItem}
            existingItems={itemsData?.data ?? []}
            setEditingItem={setEditingItem}
            itemsRefetch={() => {
              itemsRefetch();
              refetch();
            }}
            oemNo={licenseOemNo}
            isOemLicense={isOemLicense}
          />
          <ConfirmModal
            showModal={showItemsDeleteModal}
            setShowModal={setShowItemsDeleteModal}
            onDeleteConfirm={onDeleteItemsConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </>
      ) : null}

      {!isModalForm && (
        <AccountCreationModal
          isOpenModal={isOpenModal}
          modalType={modalType}
          setIsOpenModal={setIsOpenModal}
          setDefaultData={setCreatedOem}
          defaultData={createdOem}
          setModalType={setModalType}
          isModal={true}
          title={
            <div className="flex items-center bg-gray-200 p-2 rounded-md">
              {modalType === "OEM" ? (
                <OemIcon className="text-[15px] text-black ml-1" />
              ) : null}
              <span className="text-[14px] font-[Poppins] text-black ml-2">
                {`Add ${modalType}`}
              </span>
            </div>
          }
        />
      )}

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
